import type { CSSProperties, ReactNode } from 'react';
import { DEFAULT_USER_STATUS, type UserStatus } from '@allemnii-pulse/shared';
import { cn } from '../lib/utils.js';
import { useUserProfile } from '../lib/users.js';

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  online: 'Online',
  busy: 'Busy',
  offline: 'Offline',
};

export const USER_STATUS_COLOR: Record<UserStatus, string> = {
  online: 'var(--presence-online)',
  busy: 'var(--presence-busy)',
  offline: 'var(--presence-offline)',
};

/**
 * The one place a user status maps to a colour and a shape — see --presence-* in globals.css.
 * The shape carries it without colour: online is a solid dot, busy a dot with a bar through it
 * ("do not disturb"), offline a hollow ring. `ringColor` draws a gap in the colour of whatever
 * the dot sits on, for when it overlaps an avatar.
 */
export function StatusDot({
  status,
  ringColor,
  className,
}: {
  status: UserStatus;
  ringColor?: string;
  className?: string;
}) {
  const color = USER_STATUS_COLOR[status];
  const shadows = [
    status === 'offline' ? `inset 0 0 0 1.5px ${color}` : null,
    ringColor ? `0 0 0 2px ${ringColor}` : null,
  ].filter(Boolean);
  const style: CSSProperties = {
    backgroundColor: status === 'offline' ? (ringColor ?? 'transparent') : color,
    boxShadow: shadows.join(', ') || undefined,
  };

  return (
    <span
      role="img"
      aria-label={USER_STATUS_LABEL[status]}
      title={USER_STATUS_LABEL[status]}
      className={cn(
        'inline-flex h-2 w-2 shrink-0 items-center justify-center rounded-full',
        className,
      )}
      style={style}
    >
      {status === 'busy' && <span className="h-px w-[60%] rounded-full bg-white" />}
    </span>
  );
}

/** An avatar with the status dot on its bottom-right edge (none while `status` is null). The
 * dot sits outside the avatar itself, which clips its children to a circle. */
export function AvatarWithStatus({
  status,
  ringColor = 'var(--surface)',
  className,
  children,
}: {
  status: UserStatus | null;
  ringColor?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {children}
      {status && (
        <StatusDot
          status={status}
          ringColor={ringColor}
          className="absolute -bottom-px -right-px h-2 w-2"
        />
      )}
    </span>
  );
}

/** Live status dot for a uid — for lists that render names from a one-time profile lookup
 * (org and project member lists) rather than through UserName. Renders an empty slot of the
 * same size until the profile loads, so rows don't shift or flash "offline". */
export function UserStatusDot({ uid, className }: { uid: string; className?: string }) {
  const { data: profile, loading } = useUserProfile(uid);
  if (loading) return <span className={cn('inline-block h-2 w-2 shrink-0', className)} />;
  return <StatusDot status={profile?.status ?? DEFAULT_USER_STATUS} className={className} />;
}
